import React from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import { FileText, Image as ImageIcon, Trash2, Zap } from 'lucide-react';
import { TERMS_TEMPLATES } from '../../../../helper/bom';

const TermsAndDrawingTab = ({
  terms,
  setTerms,
  drawing,
  setDrawing
}) => {

  const loadTemplate = (key) => {
    const tpl = TERMS_TEMPLATES[key];
    if (tpl) {
      setTerms(JSON.parse(JSON.stringify(tpl)));
    }
  };

  const clearTerms = () => {
    setTerms([]);
  };

  const addTermClause = () => {
    setTerms([...terms, { clause_title: "", clause_content: "" }]);
  };

  const removeTermClause = (idx) => {
    setTerms(terms.filter((_, i) => i !== idx));
  };

  const updateTerm = (idx, field, val) => {
    setTerms(prev => (prev || []).map((t, i) => {
      if (i !== idx) return t;
      return { ...t, [field]: val };
    }));
  };

  return (
    <Row className="g-3">
      {/* Terms and Conditions */}
      <Col md={7}>
        <div className="border rounded-3 bg-white p-3 shadow-sm h-100" style={{ borderColor: "#cbd5e1" }}>
          <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom flex-wrap gap-2">
            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2 text-dark" style={{ fontSize: "13.5px" }}>
              <FileText size={16} className="text-primary" />
              General Terms & Conditions ({terms.length})
            </h6>
            <div className="d-flex align-items-center gap-1">
              <Button
                variant="outline-primary"
                size="sm"
                className="py-1 px-2 fw-semibold"
                style={{ fontSize: "11.5px" }}
                onClick={() => loadTemplate('ELECTRICAL_PANEL')}
              >
                <Zap size={12} className="me-1" /> Electrical Preset
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="py-1 px-2 fw-semibold"
                style={{ fontSize: "11.5px" }}
                onClick={() => loadTemplate('FABRICATION_SUPPLY')}
              >
                <Zap size={12} className="me-1" /> Fab Preset
              </Button>
              <Button
                variant="outline-success"
                size="sm"
                className="py-1 px-2 fw-semibold"
                style={{ fontSize: "11.5px" }}
                onClick={addTermClause}
              >
                + Add Clause
              </Button>
              {terms.length > 0 && (
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm p-1 border-0"
                  onClick={clearTerms}
                  title="Clear terms"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          </div>

          <div style={{ maxHeight: "360px", overflowY: "auto" }}>
            {terms.length === 0 ? (
              <div className="text-center py-4 text-muted small bg-light rounded border" style={{ fontSize: "12px" }}>
                No terms added. Click <strong>"⚡ Electrical Preset"</strong> or <strong>"+ Add Clause"</strong> above.
              </div>
            ) : (
              terms.map((term, idx) => (
                <div key={idx} className="p-2 border rounded mb-2 bg-light d-flex gap-2 align-items-start" style={{ borderColor: "#e2e8f0" }}>
                  <span className="text-muted fw-bold mt-1" style={{ fontSize: "11.5px" }}>{idx + 1}.</span>
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      className="form-control form-control-sm fw-bold mb-1 py-1"
                      style={{ height: "30px", fontSize: "12px" }}
                      placeholder="Clause Title (e.g. PRICE, VALIDITY, DELIVERY)"
                      value={term.clause_title}
                      onChange={(e) => updateTerm(idx, 'clause_title', e.target.value)}
                    />
                    <textarea
                      rows={2}
                      className="form-control form-control-sm py-1"
                      style={{ fontSize: "12px" }}
                      placeholder="Clause description and terms..."
                      value={term.clause_content}
                      onChange={(e) => updateTerm(idx, 'clause_content', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm p-1 border-0"
                    onClick={() => removeTermClause(idx)}
                    title="Delete Clause"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </Col>

      {/* GA Drawing & Dimensions */}
      <Col md={5}>
        <div className="border rounded-3 bg-white p-3 shadow-sm h-100" style={{ borderColor: "#cbd5e1" }}>
          <div className="d-flex align-items-center mb-3 pb-2 border-bottom">
            <h6 className="mb-0 fw-bold d-flex align-items-center gap-2 text-dark" style={{ fontSize: "13.5px" }}>
              <ImageIcon size={16} className="text-primary" />
              General Arrangement (GA) Drawing Reference
            </h6>
          </div>

          <Row className="g-2 mb-2">
            <Col sm={6}>
              <div>
                <label className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Drawing No:</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ height: "32px", fontSize: "12px" }}
                  placeholder="CESDWG2526114"
                  value={drawing.drawing_no || ''}
                  onChange={(e) => setDrawing({ ...drawing, drawing_no: e.target.value })}
                />
              </div>
            </Col>
            <Col sm={6}>
              <div>
                <label className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Revision:</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ height: "32px", fontSize: "12px" }}
                  placeholder="00"
                  value={drawing.revision || ''}
                  onChange={(e) => setDrawing({ ...drawing, revision: e.target.value })}
                />
              </div>
            </Col>
            <Col sm={12}>
              <div>
                <label className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Drawing Title:</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  style={{ height: "32px", fontSize: "12px" }}
                  placeholder="e.g. GENERAL ARRANGEMENT & BUSBAR ROUTING"
                  value={drawing.title || ''}
                  onChange={(e) => setDrawing({ ...drawing, title: e.target.value })}
                />
              </div>
            </Col>
          </Row>

          <span className="fw-semibold text-dark d-block mb-1 mt-2" style={{ fontSize: "12px" }}>Overall Dimensions (mm)</span>
          <Row className="g-1 mb-2">
            <Col sm={3}>
              <div>
                <label className="text-muted" style={{ fontSize: "11px" }}>Height:</label>
                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  style={{ height: "30px", fontSize: "12px" }}
                  placeholder="1000"
                  value={drawing.dimensions?.height || ''}
                  onChange={(e) => setDrawing({
                    ...drawing,
                    dimensions: { ...drawing.dimensions, height: e.target.value }
                  })}
                />
              </div>
            </Col>
            <Col sm={3}>
              <div>
                <label className="text-muted" style={{ fontSize: "11px" }}>Width:</label>
                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  style={{ height: "30px", fontSize: "12px" }}
                  placeholder="400"
                  value={drawing.dimensions?.width || ''}
                  onChange={(e) => setDrawing({
                    ...drawing,
                    dimensions: { ...drawing.dimensions, width: e.target.value }
                  })}
                />
              </div>
            </Col>
            <Col sm={3}>
              <div>
                <label className="text-muted" style={{ fontSize: "11px" }}>Depth:</label>
                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  style={{ height: "30px", fontSize: "12px" }}
                  placeholder="500"
                  value={drawing.dimensions?.depth || ''}
                  onChange={(e) => setDrawing({
                    ...drawing,
                    dimensions: { ...drawing.dimensions, depth: e.target.value }
                  })}
                />
              </div>
            </Col>
            <Col sm={3}>
              <div>
                <label className="text-muted" style={{ fontSize: "11px" }}>Stand:</label>
                <input
                  type="text"
                  className="form-control form-control-sm text-center"
                  style={{ height: "30px", fontSize: "12px" }}
                  placeholder="300"
                  value={drawing.dimensions?.base_height || ''}
                  onChange={(e) => setDrawing({
                    ...drawing,
                    dimensions: { ...drawing.dimensions, base_height: e.target.value }
                  })}
                />
              </div>
            </Col>
          </Row>

          <div>
            <label className="fw-semibold text-dark mb-1" style={{ fontSize: "12px" }}>Notes / CAD Reference:</label>
            <textarea
              rows={2}
              className="form-control form-control-sm py-1"
              style={{ fontSize: "12px" }}
              placeholder="e.g. Floor mounted compartmentalized execution, cable entry from bottom..."
              value={drawing.notes || ''}
              onChange={(e) => setDrawing({ ...drawing, notes: e.target.value })}
            />
          </div>
        </div>
      </Col>
    </Row>
  );
};

export default TermsAndDrawingTab;
